import type { AnnouncementFormValues } from '@/state/useAnnouncement';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Globe, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { AnnouncementBar } from '@/components/AnnouncementBar';
import { useAnnouncementAdmin } from '@/state/useAnnouncement';
import { EmptyState, Panel } from '../../Dashboard/ConsolePanel';
import { SeverityPicker } from './SeverityPicker';

const FIELD_INPUT = 'border-line bg-field text-foreground h-10 rounded-lg text-body';
const FIELD_TEXTAREA = 'border-line bg-field text-foreground rounded-lg text-body';

const EMPTY_FORM: AnnouncementFormValues = {
  active: false,
  severity: 'neutral',
  title: '',
  message: '',
  ctaLabel: '',
  ctaUrl: '',
};

export const Announcement = () => {
  const { announcement, isLoading, save } = useAnnouncementAdmin();
  const [form, setForm] = useState<AnnouncementFormValues>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!announcement) return;
    setForm({
      active: announcement.active,
      severity: announcement.severity,
      title: announcement.title ?? '',
      message: announcement.message ?? '',
      ctaLabel: announcement.ctaLabel ?? '',
      ctaUrl: announcement.ctaUrl ?? '',
    });
  }, [announcement]);

  const update = (values: Partial<AnnouncementFormValues>) => setForm((current) => ({ ...current, ...values }));

  const persist = async (values: AnnouncementFormValues, successMessage: string) => {
    setIsSaving(true);
    try {
      await save(values);
      toast.success(successMessage);
    } catch (ex) {
      // The API rejects an unsafe CTA link and an empty published title, and
      // the reason is the useful part.
      const message = (ex as { message?: string })?.message;
      toast.error(message || 'Could not save the announcement');
      throw ex;
    } finally {
      setIsSaving(false);
    }
  };

  // The switch saves on toggle while everything else waits for Save: taking a
  // bad announcement down should be one click, not edit-then-confirm.
  const toggleActive = async (active: boolean) => {
    const values = { ...form, active };
    const previous = form;
    update({ active });
    try {
      await persist(values, active ? 'Announcement is live' : 'Announcement taken down');
    } catch {
      setForm(previous);
    }
  };

  const handleSave = async () => {
    try {
      await persist(form, 'Announcement saved');
    } catch {
      // persist has already surfaced the reason.
    }
  };

  if (isLoading) return <Skeleton className="bg-panel2 h-96 w-full rounded-panel" />;

  return (
    <div className="flex w-full max-w-3xl flex-col gap-[14px]">
      <Panel
        title={form.active ? 'Live for everyone' : 'Not showing'}
        description={
          form.active
            ? 'This bar is at the top of every page right now.'
            : 'Turn this on to show the bar across the product.'
        }
        // The switch is the panel's only control and has no visible label — the panel
        // title reads as a status ("Live for everyone"), not as a name for the toggle.
        actions={
          <Switch
            aria-label="Show the announcement bar across the product"
            checked={form.active}
            disabled={isSaving}
            onCheckedChange={toggleActive}
          />
        }
      />

      <Panel title="Preview">
        {form.title ? (
          // No frame of its own: the bar now draws as a rounded panel with its own
          // border, so a wrapper here would double-frame it.
          <AnnouncementBar announcement={{ ...form, updatedAt: null }} onDismiss={() => undefined} />
        ) : (
          <EmptyState>Add a title to see how the bar will look.</EmptyState>
        )}
      </Panel>

      <Panel
        title="Content"
        actions={
          <Button onClick={handleSave} disabled={isSaving} className="h-9 rounded-lg">
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        }
        bodyClassName="flex flex-col gap-4"
      >
        <SeverityPicker value={form.severity} onChange={(severity) => update({ severity })} />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="announcement-title" className="micro-label block">
            Title
          </label>
          <Input
            id="announcement-title"
            value={form.title}
            maxLength={128}
            placeholder="Scheduled maintenance"
            onChange={(ev) => update({ title: ev.target.value })}
            className={FIELD_INPUT}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="announcement-message" className="micro-label block">
            Message
          </label>
          <Textarea
            id="announcement-message"
            value={form.message}
            maxLength={2000}
            rows={3}
            placeholder="Reporting will be unavailable between 02:00 and 04:00 UTC."
            onChange={(ev) => update({ message: ev.target.value })}
            className={FIELD_TEXTAREA}
          />
          <p className="supporting m-0 flex items-start gap-1.5">
            <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            This bar shows inside the product only. The signin page has its own panel, edited under Admin Tools → Signin
            Panel. The endpoint serving this text is still reachable without a session, so treat it as public.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="announcement-cta-label" className="micro-label block">
              Button label (optional)
            </label>
            <Input
              id="announcement-cta-label"
              value={form.ctaLabel}
              maxLength={64}
              placeholder="Status page"
              onChange={(ev) => update({ ctaLabel: ev.target.value })}
              className={FIELD_INPUT}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="announcement-cta-url" className="micro-label block">
              Button link (optional)
            </label>
            <Input
              id="announcement-cta-url"
              value={form.ctaUrl}
              maxLength={512}
              placeholder="https://status.pertento.ai"
              onChange={(ev) => update({ ctaUrl: ev.target.value })}
              className={FIELD_INPUT}
            />
            <p className="supporting m-0">An https:// address, or an internal path such as /settings.</p>
          </div>
        </div>
      </Panel>
    </div>
  );
};
